import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useEmployeeSession } from "@/hooks/useEmployeeSession";
import { useMonteurT } from "@/i18n/monteur";
import { ArrowLeft, Check, Lock, Mail, User, Globe } from "lucide-react";

/**
 * MITARBEITER-EINSTELLUNGEN · Teil 2 (Juni 2026, betreibergenehmigt).
 * Dunkler Hub-Stil (wie MonteurHome): Profil (read-only), freiwillige E-Mail
 * für Benachrichtigungen (jederzeit änder-/löschbar, Zweckbindung + Link auf
 * /datenschutz) und Sprache (bestehende 7-Sprachen-Logik, wirkt sofort).
 * PIN-Änderung bewusst NICHT enthalten (spätere Phase).
 */
const LANGS = [
  ["de", "Deutsch"],
  ["en", "English"],
  ["pl", "Polski"],
  ["uk", "Українська"],
  ["bg", "Български"],
  ["ro", "Română"],
  ["tr", "Türkçe"],
  ["hr", "Hrvatski"],
  ["ru", "Русский"],
];

function SectionCard({ icon: Icon, title, children, testid }) {
  return (
    <div className="rounded-2xl bg-white px-4 py-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-slate-500" />
        <h2 className="font-heading font-semibold text-slate-900 text-base">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function MonteurEinstellungen() {
  const navigate = useNavigate();
  const { session, refresh } = useEmployeeSession();
  const t = useMonteurT();
  const employee = session?.employee;

  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState(null); // null | "saved" | "invalid" | "error"
  const [saving, setSaving] = useState(false);
  const [langSaving, setLangSaving] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);

  useEffect(() => {
    setEmail(employee?.email || "");
  }, [employee?.email]);

  const emailDirty = (email || "") !== (employee?.email || "");

  const saveEmail = async () => {
    setSaving(true);
    setEmailStatus(null);
    try {
      await api.updateMySettings({ email: email.trim() });
      await refresh();
      setEmailStatus("saved");
      setTimeout(() => setEmailStatus(null), 3000);
    } catch (e) {
      setEmailStatus(e?.response?.status === 400 ? "invalid" : "error");
    } finally {
      setSaving(false);
    }
  };

  const consentActive = !!employee?.notify_consent_at;

  // Sprach-Entitlement (Juni 2026): nur "de" + vom Mandanten gebuchte Sprachen
  // sind wählbar; fehlt das Feld (alte Session), fail-closed nur Deutsch.
  const availableLangs = employee?.available_languages || ["de"];
  const anyLocked = LANGS.some(([code]) => !availableLangs.includes(code));

  const toggleConsent = async (e) => {
    const next = e.target.checked;
    setConsentSaving(true);
    try {
      // Eigenständiger Zustand — bewusst getrennt vom E-Mail-Speichern.
      await api.updateMySettings({ notify_consent: next });
      await refresh();
    } catch {
      /* fail-soft: Checkbox bleibt auf Serverstand */
    } finally {
      setConsentSaving(false);
    }
  };

  const selectLanguage = async (code) => {
    if (langSaving || code === employee?.preferred_language) return;
    if (!availableLangs.includes(code)) return;
    setLangSaving(true);
    try {
      await api.updateMySettings({ preferred_language: code });
      // Session-Refresh: multilanguage_enabled ist abgeleitet → UI schaltet sofort um.
      await refresh();
    } catch {
      /* fail-soft: Auswahl bleibt auf Serverstand */
    } finally {
      setLangSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="monteur-einstellungen">
      <header className="max-w-lg mx-auto px-5 pt-6 pb-4 flex items-center gap-3">
        <button
          onClick={() => navigate("/monteur")}
          data-testid="monteur-einstellungen-back"
          aria-label={t("tasks.backAria")}
          className="text-slate-400 hover:text-white p-2 -ml-2 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-heading text-lg font-bold text-white">
          {t("home.settingsTitle")}
        </h1>
      </header>

      <div className="max-w-lg mx-auto px-5 pb-10 space-y-3">
        {/* Profil — reine Anzeige, bewusst ohne Eingabefelder. */}
        <SectionCard icon={User} title={t("settings.profileTitle")} testid="settings-profile-card">
          <div className="flex items-center gap-3">
            {employee?.photo_url && (
              <img
                src={employee.photo_url}
                alt=""
                className="w-10 h-10 rounded-full object-cover bg-slate-200 shrink-0"
              />
            )}
            <div className="min-w-0 text-sm">
              <div className="font-semibold text-slate-900" data-testid="settings-profile-name">
                {employee ? `${employee.first_name} ${employee.last_name}`.trim() : ""}
              </div>
              <div className="text-slate-500" data-testid="settings-profile-company">
                {employee?.company_name || t("tasks.companyFallback")}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">{t("settings.profileHint")}</p>
        </SectionCard>

        {/* E-Mail für Benachrichtigungen — freiwillig, jederzeit löschbar. */}
        <SectionCard icon={Mail} title={t("settings.emailTitle")} testid="settings-email-card">
          <p className="text-xs text-slate-500 mb-3">
            {t("settings.emailPurpose")}{" "}
            <a
              href="/datenschutz"
              target="_blank"
              rel="noreferrer"
              className="underline text-slate-600 hover:text-slate-900"
              data-testid="settings-privacy-link"
            >
              {t("settings.privacyLink")}
            </a>
          </p>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailStatus(null);
            }}
            placeholder={t("settings.emailPlaceholder")}
            data-testid="settings-email-input"
            className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          />
          <p className="text-[11px] text-slate-400 mt-2">{t("settings.emailRemoveHint")}</p>
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={saveEmail}
              disabled={!emailDirty || saving}
              data-testid="settings-email-save"
              className="rounded-xl bg-slate-900 text-white text-sm font-semibold px-4 py-2.5 disabled:opacity-40 hover:bg-slate-700 transition-colors"
            >
              {t("settings.emailSave")}
            </button>
            {emailStatus === "saved" && (
              <span className="text-sm text-emerald-600 flex items-center gap-1" data-testid="settings-email-saved">
                <Check className="w-4 h-4" /> {t("settings.emailSaved")}
              </span>
            )}
            {emailStatus === "invalid" && (
              <span className="text-sm text-red-600" data-testid="settings-email-invalid">
                {t("settings.emailInvalid")}
              </span>
            )}
            {emailStatus === "error" && (
              <span className="text-sm text-red-600" data-testid="settings-email-error">
                {t("settings.saveError")}
              </span>
            )}
          </div>
          {/* Einwilligung (Juni 2026): eigener, von der Adresse unabhängiger,
              einzeln widerrufbarer Zustand (notify_consent_at am Employee). */}
          <label
            className="mt-4 flex items-start gap-2.5 cursor-pointer select-none border-t border-slate-100 pt-3"
            data-testid="settings-notify-consent-label"
          >
            <input
              type="checkbox"
              checked={consentActive}
              onChange={toggleConsent}
              disabled={consentSaving}
              data-testid="settings-notify-consent-checkbox"
              className="mt-0.5 w-4 h-4 shrink-0 accent-slate-900 disabled:opacity-60"
            />
            <span className="text-xs text-slate-600">{t("settings.consentLabel")}</span>
          </label>
        </SectionCard>

        {/* Sprache — immer sichtbar; nicht gebuchte Sprachen gesperrt (Juni 2026). */}
        <SectionCard icon={Globe} title={t("settings.languageTitle")} testid="settings-language-card">
          <div className="divide-y divide-slate-100">
            {LANGS.map(([code, label]) => {
              const active = (employee?.preferred_language || "de") === code;
              const locked = !availableLangs.includes(code);
              return (
                <button
                  key={code}
                  onClick={() => selectLanguage(code)}
                  disabled={langSaving || locked}
                  data-testid={`settings-lang-${code}`}
                  aria-pressed={active}
                  aria-disabled={locked}
                  className={`w-full flex items-center justify-between py-2.5 text-sm text-left disabled:opacity-60 ${
                    locked ? "opacity-40 cursor-not-allowed" : ""
                  }`}
                >
                  <span className={active ? "font-semibold text-slate-900" : "text-slate-600"}>
                    {label}
                  </span>
                  {active && (
                    <span data-testid={`settings-lang-${code}-active`} className="text-emerald-600">
                      <Check className="w-4 h-4" />
                    </span>
                  )}
                  {locked && (
                    <span data-testid={`settings-lang-${code}-locked`} className="text-slate-400">
                      <Lock className="w-3.5 h-3.5" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {anyLocked && (
            <p
              className="text-[11px] text-slate-400 mt-3 flex items-center gap-1.5"
              data-testid="settings-lang-not-booked-hint"
            >
              <Lock className="w-3 h-3 shrink-0" />
              {t("settings.langNotBooked")}
            </p>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
